import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ViewChild, ElementRef, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { BaseInputDirective } from '../base-input.directive';
import { SelectListService } from '../../services/form-utility/select-list.service';
import { ISelectOptionGroup } from '../../models/select-option-group';
import { ISelectOption } from '../../models/select-option';
import { BehaviorSubject, fromEvent, merge, Observable, Subscription } from 'rxjs';
import { first, startWith, map } from 'rxjs/operators';
import { ViewEncapsulation } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-autocomplete-input',
  templateUrl: './autocomplete-input.component.html',
  styleUrls: ['./autocomplete-input.component.scss'],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatAutocompleteModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
  ],
})
export class AutoCompleteInputComponent extends BaseInputDirective implements OnInit, OnDestroy {
  private selectListService = inject(SelectListService);

  @Input() fetchData: Observable<ISelectOptionGroup[]>;
  @Input() nameFormatter: (obj: object) => string;
  floatLabel = 'auto';
  @Output() richChange = new EventEmitter();
  @Input() showSingleGroup = false;
  @Input() useCodeValue = false;
  @Input() allowCustomSelection = false;
  @Input() searchFullText = true;
  @Input() allowNullSelection = true;
  @Input() minimumCharacters = 0;
  @Input() alwaysShowAutocomplete = false;
  @Input() invalidValueErrorMessage: string =
    'The criteria entered did not match a value in our system.';
  @Output() fetchDataError = new EventEmitter();

  @ViewChild('input') private input: ElementRef;

  optionGroups: ISelectOptionGroup[];
  filteredGroups: ISelectOptionGroup[] = [];
  flatOptions: ISelectOption[];
  filteredOptions: Observable<ISelectOption[]>;
  hasOneGroup = false;
  currentSelection: string;
  selectedOptions: ISelectOption[];
  showAutocomplete = false;
  private hasSelectList: BehaviorSubject<boolean> = new BehaviorSubject(false);

  connected: Subscription;

  private initialValue: any;
  private changeSubscription: Subscription;

  readonly DEFAULT_PLACEHOLDER = '-Begin Typing-';

  
 



  ngOnInit() {
    super.ngOnInit();

    // Track if network connection is present, and if not mark input as invalid to prevent getting around validation
    // when offline.
    const online = fromEvent(window, 'online').pipe(map(() => true));
    const offline = fromEvent(window, 'offline').pipe(map(() => false));
    this.connected = merge(online, offline).subscribe(conn => {
      if (conn) {
        this.setAsValid();
        this.loadOptions(); // reload options when connection re-established
      } else {
        this.setAsInvalid();
      }
    });

    this.initialValue = this.formControl.value;

    this.changeSubscription = this.formControl.valueChanges.subscribe(value => {
      if (this.flatOptions) {
        this.selectedOptions = this.selectListService.getCurrentSelection(this.flatOptions, value);
        this.currentSelection = this.selectedOptions.map(x => x.name).join(', ');
        if (value && value + '' !== '' && this.currentSelection + '' === '') {
          this.formControl.setErrors({
            notValidOption: true,
          });
        } else {
          const currentErrors = this.formControl.errors;
          if (!!currentErrors && !!currentErrors['notValidOption']) {
            delete currentErrors.notValidOption;
            this.formControl.setErrors(currentErrors);
          }
        }
      }
    });

    this.filteredOptions = this.formControl.valueChanges.pipe(
      startWith(''),
      map(value => (typeof value === 'string' ? value : null)),
      // eslint-disable-next-line no-confusing-arrow
      map(name =>
        (name?.length | 0) >= this.minimumCharacters
          ? name
            ? this.textFilter(name)
            : this.flatOptions?.slice()
          : [],
      ),
    );

    if (!this.placeholder) {
      this.placeholder = this.DEFAULT_PLACEHOLDER;
    }
    this.floatLabel = 'always';

    this.loadOptions();
  }

  refreshOptions() {
    this.loadOptions();
  }

  displayFn = (option?: number): string | undefined => {
    if (this.flatOptions && option) {
      this.selectedOptions = this.selectListService.getCurrentSelection(this.flatOptions, option);
      return this.selectedOptions.map(x => x.name).join(', ');
    }
    return undefined;
  };

  private setAsInvalid() {
    if (!this.formControl.errors || !this.formControl.errors['notValidOption']) {
      this.formControl.setErrors({ notValidOption: true });
    }
  }

  private setAsValid() {
    const currentErrors = this.formControl.errors;
    if (!!currentErrors && !!currentErrors['notValidOption']) {
      delete currentErrors.notValidOption;
      this.formControl.setErrors(currentErrors);
    }
  }

  private loadOptions(suppressErrors: boolean = false) {
    const dataFetcher = this.fetchData || this.defaultDataFetcher(this.fgInfo?.optionList);

    dataFetcher.subscribe(
      selectOptionGroups => {
        if (this.useCodeValue) {
          selectOptionGroups = selectOptionGroups.map(this.mapSelectListGroupForCodeValue, this);
        }
        this.optionGroups = this.deepCopy(selectOptionGroups);
        this.flatOptions = this.selectListService.flattenOptions(this.optionGroups);
        this.selectedOptions = this.selectListService.getCurrentSelection(
          this.flatOptions,
          this.formControl.value,
        );
        this.currentSelection = this.selectedOptions.map(x => x.name).join(', ');
        this.formControl.patchValue(this.formControl.value);
        this.validateSelection();
        if (this.optionGroups) {
          // Filter out inactive and not-selected options
          this.calculateOptions();
        }

        this.hasSelectList.next(true);
      },
      (error: HttpErrorResponse) => {
        if (!suppressErrors) {
          this.fetchDataError.emit();
        }

        // retry after several seconds if connection was lost
        if (error.status === 504) {
          setTimeout(() => {
            this.loadOptions(true);
          }, 5000);
        }
      },
    );
  }

  focus() {
    this.input.nativeElement.focus();
  }

  private textFilter(name: string): ISelectOption[] {
    if (this.flatOptions) {
      const filterValue = name.toLowerCase();
      if (this.searchFullText) {
        return this.flatOptions.filter(option => option.name.toLowerCase().includes(filterValue));
      } else {
        return this.flatOptions.filter(
          option => option.name.toLowerCase().indexOf(filterValue) === 0,
        );
      }
    } else {
      return [];
    }
  }

  private defaultDataFetcher(optionList: string) {
    return this.selectListService
      .getSelectList(optionList, this.nameFormatter)
      .pipe(first(response => response !== null));
  }

  private calculateOptions(): any {
    this.filteredGroups = [];
    this.filteredGroups = this.selectListService.filterAndSortSelectOptionGroup(this.optionGroups);

    if (this.optionGroups.length === 1 && !this.showSingleGroup) {
      this.hasOneGroup = true;
    }
  }

  activateOption(value: any) {
    this.hasSelectList.pipe(first(hasList => hasList === true)).subscribe(() => {
      this.updateOption(value, true);
    });
  }

  deactivateOption(value: any) {
    this.hasSelectList.pipe(first(hasList => hasList === true)).subscribe(() => {
      this.updateOption(value, false);
    });
  }

  private updateOption(value: any, isDeleted: boolean) {
    if (this.optionGroups) {
      for (const group of this.optionGroups) {
        if (group.options) {
          for (const option of group.options) {
            if (option.value === value && option.isDeleted !== isDeleted) {
              option.isDeleted = isDeleted;

              this.calculateOptions();

              return;
            }
          }
        }
      }
    }
  }

  onInputClick(event) {
    if (event.target.value !== null && event.target.value.length === 0) {
      this.showAutocomplete = true;
    }
  }

  onInputKeyDown(event) {
    if (event.key === 'Enter' || event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      this.showAutocomplete = true;
    }
  }

  onInputFocusOut(event) {
    // Hide the autocomplete if we can't confirm focusing on another option (assumed to be part of this autocomplete)
    const relatedTarget: Element = event?.relatedTarget;
    if (!(relatedTarget?.className?.indexOf('mat-option') >= 0)) {
      this.showAutocomplete = false;
    }
  }

  onInputValueUpdate(event) {
    if (
      (event === '' && this.minimumCharacters === 0) ||
      (event && event.length >= this.minimumCharacters)
    ) {
      this.showAutocomplete = true;
    } else {
      this.showAutocomplete = false;
    }
  }

  onChange() {
    // TODO: Possible optimizations to prevent getCurrentSelection from calling twice (onChange and valueChanges)
    this.selectedOptions = this.selectListService.getCurrentSelection(
      this.flatOptions,
      this.formControl.value,
    );
    this.currentSelection = this.selectedOptions.map(x => x.name).join(', ');
  }

  validateSelection() {
    if (
      (!this.selectedOptions || this.selectedOptions.length === 0) &&
      !this.allowCustomSelection
    ) {
      // option isn't valid and we're not allowing custom selections, reset the form field
      this.formControl.setValue(null);
    } else {
      // option is valid or we're allowing custom selections, emit the change
      this.change.emit({ value: this.formControl.value, name: this.currentSelection });
      this.richChange.next(this.selectedOptions);
    }
  }

  deepCopy<T>(obj: T): T {
    let copy;

    // Handle the 3 simple types, and null or undefined
    if (null == obj || 'object' !== typeof obj) {
      return obj;
    }

    // Handle Date
    if (obj instanceof Date) {
      copy = new Date();
      copy.setTime(obj.getTime());
      return copy;
    }

    // Handle Array
    if (obj instanceof Array) {
      copy = [];
      for (let i = 0, len = obj.length; i < len; i++) {
        copy[i] = this.deepCopy(obj[i]);
      }
      return copy;
    }

    // Handle Object
    if (obj instanceof Object) {
      copy = {};
      for (const attr in obj) {
        if (obj.hasOwnProperty(attr)) {
          copy[attr] = this.deepCopy(obj[attr]);
        }
      }
      return copy;
    }

    throw new Error('Unable to copy obj! Its type is not supported.');
  }

  ngOnDestroy(): void {
    this.changeSubscription?.unsubscribe();
  }

  private mapSelectListGroupForCodeValue(group: ISelectOptionGroup): ISelectOptionGroup {
    return {
      name: group.name,
      options: group.options.map(this.mapSelectListOptionsForCodeValue),
      sortOrder: group.sortOrder,
    };
  }

  private mapSelectListOptionsForCodeValue(option: ISelectOption): ISelectOption {
    return {
      name: option.name,
      value: option.code,
      sortOrder: option.sortOrder,
      isDeleted: option.isDeleted,
      parentId: option.parentId,
      data: option.data,
      code: option.value,
    };
  }
}
